import { useTranslation } from "react-i18next";
import type { SocialProofItem } from "~/components/reviews/ReviewsCarousel";
import { StarRating } from "~/components/ui/star-rating";
import type { ProductAppRating } from "~/data/products/types";
import amazonIcon from "~/assets/reviews/amazon-icon.svg";
import appleIcon from "~/assets/reviews/apple-icon.svg";
import googlePlayIcon from "~/assets/reviews/google-play-icon.svg";

export type StoreRating = ProductAppRating;
type Store = StoreRating["store"];

const STORE_ICONS: Record<Store, { src: string; className: string }> = {
  amazon: { src: amazonIcon, className: "size-4 md:size-5" },
  apple: { src: appleIcon, className: "h-4 w-[12.75px] md:h-[18px] md:w-[14.34px]" },
  google: { src: googlePlayIcon, className: "h-[14px] w-[12.5px] md:h-4 md:w-[14.29px]" },
};

interface StoreRatingBlockProps {
  rating: StoreRating;
}

// Icon + rating on one line, then stars, then the review count. Ratings are live
// madisongwapi data, so stars are computed rather than a pre-rendered strip.
function StoreRatingBlock({ rating }: StoreRatingBlockProps) {
  const { t, i18n } = useTranslation("common");
  const icon = STORE_ICONS[rating.store];
  const ratingText = new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
    rating.rating,
  );
  const countText = new Intl.NumberFormat(i18n.language).format(rating.reviewCount);

  return (
    <div className="flex w-[88px] flex-col items-center gap-2 md:w-[122px]">
      <div className="flex items-center justify-center gap-4">
        {/* eslint-disable-next-line local/no-raw-img -- store badges are always SVGs */}
        <img alt={t(`store_ratings.${rating.store}`)} src={icon.src} className={icon.className} />
        {/* text-xl is this project's 24px (Figma titles/sm); text-md (18px) is the mobile size. */}
        <p className="font-display text-text-heading text-md leading-[1.15] font-medium tracking-[0.24px] md:text-xl">
          {ratingText}
        </p>
      </div>
      <StarRating rating={rating.rating} compact color="text-bouncie-500" className="gap-px md:gap-[3px]" />
      <p className="text-text-body-alt w-full text-center text-xs leading-[18px]">
        {t("store_ratings.review_count", { total: countText })}
      </p>
    </div>
  );
}

/** Maps store ratings to ReviewsCarousel social-proof items. */
export function storeRatingItems(ratings: StoreRating[]): SocialProofItem[] {
  return ratings.map((rating) => ({
    key: rating.store,
    content: <StoreRatingBlock rating={rating} />,
  }));
}
